import { useState, useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { X, Maximize2, Minimize2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

/**
 * PacerSheet — Desktop full-height right-side chat panel for the Pacer assistant.
 *
 * Slides in from the right, covers the right chrome column.
 * Left-edge drop shadow creates depth against the content.
 * Always dark (bg-slate-900) — Pacer aesthetic, light/dark host doesn't matter.
 *
 * Triggered by: Pacer orb click or Cmd+P (registered in Layout.tsx).
 * Close by: X button, ESC (Layout keyboard shortcut), or backdrop click.
 *
 * Expand (w-[420px] → w-[680px]) for longer conversations.
 *
 * Phase 8+: wire streaming responses.
 */

const PACER_URL = 'https://pacer.swimflows.com/ask'

const WIDTH_NORMAL = 420
const WIDTH_EXPANDED = 680
// Floating inset from the viewport's right edge (matches merchant-portal's right-2 sheet).
const SHEET_INSET = 8

interface Message {
  role: 'user' | 'pacer'
  text: string
}

interface HistoryItem {
  role: string
  content: string
}

interface PacerSheetProps {
  open: boolean
  onClose: () => void
}

function OrbDot() {
  return (
    <div
      className="w-4 h-4 rounded-full flex-shrink-0 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)' }}
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'linear-gradient(220deg, #fed7aa 0%, #fbbf24 30%, #e8dff5 60%, #a5f3fc 100%)',
          animation: 'pacerOrbDot 4s ease-in-out infinite',
        }}
      />
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="self-start flex gap-1.5 px-3 py-2.5 bg-slate-700/60 rounded-2xl rounded-bl-sm">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-slate-400 block"
          style={{ animation: 'pacerDotBounce 1.2s ease-in-out infinite', animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </div>
  )
}

export function PacerSheet({ open, onClose }: PacerSheetProps) {
  const { t: tCommon } = useTranslation('common')
  const { t } = useTranslation('pacer')
  const [expanded, setExpanded] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputVal, setInputVal] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const historyRef = useRef<HistoryItem[]>([])
  const backdropRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const typingRef = useRef<HTMLDivElement>(null)

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const sheetWidth = expanded ? WIDTH_EXPANDED : WIDTH_NORMAL

  function handleClose() {
    onClose()
    setExpanded(false)
  }

  // Open/close animation
  useEffect(() => {
    const backdrop = backdropRef.current
    const sheet = sheetRef.current
    if (!backdrop || !sheet) return

    if (open) {
      gsap.set(backdrop, { display: 'block' })
      gsap.set(sheet, { display: 'flex' })
      if (prefersReduced) {
        gsap.set(backdrop, { opacity: 1 })
        gsap.set(sheet, { x: 0 })
      } else {
        gsap.fromTo(backdrop, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'power2.out' })
        gsap.fromTo(sheet, { x: WIDTH_NORMAL + SHEET_INSET }, { x: 0, duration: 0.24, ease: [0.32, 0.72, 0, 1] as unknown as string })
      }
    } else {
      const closeWidth = (expanded ? WIDTH_EXPANDED : WIDTH_NORMAL) + SHEET_INSET
      if (prefersReduced) {
        gsap.set(backdrop, { display: 'none' })
        gsap.set(sheet, { display: 'none' })
      } else {
        gsap.to(backdrop, { opacity: 0, duration: 0.2, ease: 'power2.in', onComplete: () => gsap.set(backdrop, { display: 'none' }) })
        gsap.to(sheet, { x: closeWidth, duration: 0.24, ease: [0.32, 0.72, 0, 1] as unknown as string, onComplete: () => gsap.set(sheet, { display: 'none' }) })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Width transition on expand/collapse
  useEffect(() => {
    const sheet = sheetRef.current
    if (!sheet || !open) return
    if (prefersReduced) {
      gsap.set(sheet, { width: sheetWidth })
    } else {
      gsap.to(sheet, { width: sheetWidth, duration: 0.22, ease: 'power2.out' })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])

  // Scroll to bottom
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [messages, isLoading])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 200)
      return () => clearTimeout(timer)
    }
  }, [open])

  // ESC closes
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [open, onClose])

  // Typing indicator
  useEffect(() => {
    const el = typingRef.current
    if (!el) return
    if (isLoading) {
      gsap.set(el, { display: 'block' })
      if (!prefersReduced) gsap.fromTo(el, { opacity: 0, y: 4 }, { opacity: 1, y: 0, duration: 0.25, ease: 'power2.out' })
    } else {
      if (!prefersReduced) {
        gsap.to(el, { opacity: 0, y: 4, duration: 0.18, ease: 'power2.in', onComplete: () => gsap.set(el, { display: 'none' }) })
      } else {
        gsap.set(el, { display: 'none' })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  async function submit() {
    const q = inputVal.trim()
    if (!q || isLoading) return

    setInputVal('')
    setMessages((prev) => [...prev, { role: 'user', text: q }])
    historyRef.current.push({ role: 'user', content: q })
    setIsLoading(true)

    try {
      const res = await fetch(PACER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, messages: historyRef.current }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = await res.json() as { answer?: string; error?: string }
      if (data.error) throw new Error(data.error)
      const answer = data.answer ?? '(no answer)'
      setMessages((prev) => [...prev, { role: 'pacer', text: answer }])
      historyRef.current.push({ role: 'assistant', content: answer })
    } catch (e) {
      console.error('[PacerSheet] ask failed:', e)
      setMessages((prev) => [...prev, { role: 'pacer', text: t('drawer.error') }])
    } finally {
      setIsLoading(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }

  return (
    <>
      {/* Typing indicator keyframes (pacerDotBounce lives in globals.css) */}
      <style>{`
        @keyframes pacerOrbDot {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="fixed inset-0 z-40 bg-black/30 dark:bg-black/50"
        style={{ display: 'none' }}
        onClick={handleClose}
        aria-hidden
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-label={t('drawer.title')}
        aria-modal="true"
        className="fixed top-2 bottom-2 right-2 z-50 flex-col bg-slate-900 overflow-hidden rounded-2xl border border-slate-800"
        style={{ display: 'none', width: WIDTH_NORMAL, boxShadow: '-16px 0 48px rgba(0,0,0,0.35)' }}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-2.5 px-4 py-3.5 flex-shrink-0 border-b border-slate-800">
          <OrbDot />
          <span className="flex-1 text-sm font-semibold font-sans text-white">
            {t('drawer.title')}
          </span>

          <button
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? t('drawer.collapse') : t('drawer.expand')}
            className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            {expanded
              ? <Minimize2 className="w-3.5 h-3.5" aria-hidden />
              : <Maximize2 className="w-3.5 h-3.5" aria-hidden />
            }
          </button>

          <button
            onClick={handleClose}
            aria-label={t('drawer.close')}
            className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>

        {/* ── Messages ── */}
        <div
          ref={messagesRef}
          className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-3 scroll-smooth"
          aria-live="polite"
          aria-label={tCommon('aria.conversation')}
        >
          {messages.length === 0 && !isLoading && (
            <p className="text-sm font-sans text-slate-500 text-center my-auto py-8 leading-relaxed">
              {t('drawer.emptyState')}
            </p>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={cn(
                'flex flex-col gap-0.5 max-w-[88%]',
                msg.role === 'user' ? 'self-end items-end' : 'self-start items-start',
              )}
            >
              <span className={cn(
                'text-[10px] font-mono uppercase tracking-wider px-1',
                msg.role === 'user' ? 'text-ocean-400' : 'text-amber-400',
              )}>
                {msg.role === 'user' ? t('drawer.you') : t('drawer.pacer')}
              </span>
              <div className={cn(
                'px-3 py-2 rounded-2xl text-sm font-sans leading-relaxed whitespace-pre-wrap break-words',
                msg.role === 'user'
                  ? 'bg-ocean-600/30 text-white rounded-br-sm'
                  : 'bg-slate-700/60 text-sand-100 rounded-bl-sm',
              )}>
                {msg.text}
              </div>
            </div>
          ))}

          <div ref={typingRef} style={{ display: 'none' }}>
            <TypingIndicator />
          </div>
        </div>

        {/* ── Input area ── */}
        <div className="flex-shrink-0 border-t border-slate-800 px-5 pt-3 pb-5">
          <textarea
            ref={inputRef}
            value={inputVal}
            onChange={(e) => {
              setInputVal(e.target.value)
              e.target.style.height = 'auto'
              e.target.style.height = `${e.target.scrollHeight}px`
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
                e.preventDefault()
                void submit()
              }
            }}
            placeholder={t('drawer.placeholder')}
            rows={2}
            disabled={isLoading}
            className="
              w-full bg-slate-800 rounded-xl px-3 py-2.5
              text-sm text-white placeholder:text-slate-500
              border border-slate-700 focus:border-amber-500/50
              focus:outline-none resize-none font-sans leading-relaxed
              disabled:opacity-50 max-h-32 overflow-y-auto
              transition-[border-color] duration-150
            "
          />
          <div className="flex items-center justify-between mt-1.5">
            <span className={cn(
              'text-[10px] font-sans transition-colors duration-150',
              isLoading ? 'text-amber-400' : 'text-slate-500',
            )}>
              {isLoading ? t('drawer.thinking') : t('drawer.status')}
            </span>
            <span className="text-[10px] font-mono text-slate-600">
              {t('drawer.hint')}
            </span>
          </div>
        </div>
      </div>
    </>
  )
}
